/**
 * VÉRAULT Portal — Content Studio · Curated Guides
 *
 * Wave 11u — Editorial guide management. The advisor authors mini-
 * journals here that surface in the client Travel Prep section.
 *
 * Architecture:
 *   · Reads from /api/admin/curated-guides (all guides, advisor-only).
 *   · Each guide has a slug, hero image, 3-12 editorial passages,
 *     pull quote, destination_tokens (for auto-match), and
 *     assigned_trip_ids (explicit advisor override).
 *   · A guide surfaces in a client's Travel Prep only when:
 *       · the trip_id is in assigned_trip_ids, OR
 *       · at least one destination_token matches a VISIBLE segment
 *         of that trip's destinations.
 *   · published=false hides the guide from clients entirely.
 *
 * UX:
 *   · List view shows slug, title, destination tokens, # assigned trips, status.
 *   · "New guide" + per-row Edit/Delete open a full-screen editor.
 *   · Editor has hero preview, title/subtitle/passages/pull quote,
 *     destination tokens (chip input), and trip-assignment picker
 *     (search advisor's trips, multi-select).
 */
import React from "react";
import { Plus, Edit3, Trash2, Eye, EyeOff, X, Save, Loader2, MapPin, FileText, Tag as TagIcon, Search } from "lucide-react";
import {
  Surface,
  Heading,
  StatusTag,
  Empty,
  Skeleton,
  PortalButton,
  api,
} from "../../../index";
import { toast } from "sonner";

export default function ContentGuides() {
  const [guides, setGuides] = React.useState(null);
  const [editing, setEditing] = React.useState(null); // guide object or "new"
  const [query, setQuery] = React.useState("");
  const [error, setError] = React.useState(null);

  const refresh = React.useCallback(async () => {
    try {
      const { data } = await api.get("/admin/curated-guides");
      setGuides(data?.guides || []);
      setError(null);
    } catch (e) {
      setError("Couldn't load guides.");
    }
  }, []);

  React.useEffect(() => { refresh(); }, [refresh]);

  const filtered = React.useMemo(() => {
    if (!guides) return null;
    const q = query.trim().toLowerCase();
    if (!q) return guides;
    return guides.filter((g) =>
      [g.title, g.subtitle, g.slug, ...(g.destination_tokens || []), ...(g.tags || [])]
        .filter(Boolean).join(" ").toLowerCase().includes(q),
    );
  }, [guides, query]);

  const onDelete = async (slug) => {
    if (!window.confirm(`Delete the guide "${slug}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/curated-guides/${slug}`);
      toast.success("Guide removed.");
      refresh();
    } catch (e) {
      toast.error("Couldn't delete.");
    }
  };

  return (
    <div className="space-y-6" data-testid="content-guides-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />
            <input
              type="search"
              placeholder="Search guides…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 rounded-md border border-[rgba(0,0,0,0.08)] bg-white text-[13px] focus:outline-none focus:border-[hsl(var(--accent-gold))]"
              data-testid="guides-search"
            />
          </div>
        </div>
        <PortalButton
          onClick={() => setEditing("new")}
          icon={<Plus className="size-3.5" strokeWidth={1.5} />}
          data-testid="guides-new"
        >
          New guide
        </PortalButton>
      </div>

      {error && <p className="text-[13px] text-[hsl(var(--accent-terracotta))]">{error}</p>}
      {guides === null && (
        <div className="space-y-3"><Skeleton className="h-16" /><Skeleton className="h-16" /><Skeleton className="h-16" /></div>
      )}
      {filtered && filtered.length === 0 && (
        <Empty
          icon={<FileText className="size-8" strokeWidth={1.2} />}
          title={query ? "No guides match that search." : "No guides yet."}
          body={query ? "Try a different keyword." : "Create your first editorial guide — it'll surface in matching clients' Travel Prep."}
        />
      )}
      {filtered && filtered.length > 0 && (
        <div className="space-y-2.5">
          {filtered.map((g) => (
            <GuideRow
              key={g.slug}
              guide={g}
              onEdit={() => setEditing(g)}
              onDelete={() => onDelete(g.slug)}
            />
          ))}
        </div>
      )}

      {editing && (
        <GuideEditor
          guide={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}

function GuideRow({ guide, onEdit, onDelete }) {
  const tokens = guide.destination_tokens || [];
  const assignedCount = (guide.assigned_trip_ids || []).length;
  return (
    <article
      className="group flex items-stretch gap-3 rounded-lg border border-[rgba(0,0,0,0.06)] bg-white overflow-hidden hover:border-[hsl(var(--accent-gold))]/40 transition-colors"
      data-testid={`guide-row-${guide.slug}`}
    >
      <div className="w-24 h-24 sm:w-32 sm:h-32 shrink-0 bg-[hsl(var(--background-paper))] relative">
        {guide.hero_image && (
          <img src={guide.hero_image} alt={guide.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
        )}
      </div>
      <div className="flex-1 min-w-0 p-3 sm:p-4 flex flex-col justify-between">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-serif text-[16px] sm:text-[17px] text-[hsl(var(--foreground))] truncate">{guide.title}</h4>
            {guide.published ? (
              <StatusTag tone="gold">Published</StatusTag>
            ) : (
              <StatusTag tone="neutral">Draft</StatusTag>
            )}
          </div>
          {guide.subtitle && (
            <p className="mt-1 font-accent italic text-[13px] text-[hsl(var(--foreground-muted))] line-clamp-1">{guide.subtitle}</p>
          )}
          <div className="mt-2 flex items-center gap-3 flex-wrap text-[11px]">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">/{guide.slug}</span>
            {tokens.length > 0 && (
              <div className="flex items-center gap-1 flex-wrap">
                <MapPin className="size-3 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
                {tokens.slice(0, 4).map((t) => (
                  <span key={t} className="font-mono text-[9.5px] uppercase tracking-[0.16em] text-[hsl(var(--accent-gold))] bg-[hsl(var(--accent-gold))]/8 px-1.5 py-0.5 rounded">{t}</span>
                ))}
                {tokens.length > 4 && <span className="font-mono text-[10px] text-[hsl(var(--foreground-muted))]">+{tokens.length - 4}</span>}
              </div>
            )}
            {assignedCount > 0 && (
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(168,22%,32%)] bg-[hsl(168,22%,32%)]/10 px-1.5 py-0.5 rounded">
                {assignedCount} trip{assignedCount === 1 ? "" : "s"} assigned
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1 pr-3 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={onEdit}
          className="p-1.5 rounded text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--background-paper))]"
          aria-label="Edit guide"
          data-testid={`guide-edit-${guide.slug}`}
        >
          <Edit3 className="size-3.5" strokeWidth={1.5} />
        </button>
        <button
          onClick={onDelete}
          className="p-1.5 rounded text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-terracotta))] hover:bg-[hsl(var(--background-paper))]"
          aria-label="Delete guide"
          data-testid={`guide-delete-${guide.slug}`}
        >
          <Trash2 className="size-3.5" strokeWidth={1.5} />
        </button>
      </div>
    </article>
  );
}

function GuideEditor({ guide, onClose, onSaved }) {
  const isNew = !guide;
  const [form, setForm] = React.useState(() => ({
    slug: guide?.slug || "",
    title: guide?.title || "",
    subtitle: guide?.subtitle || "",
    hero_image: guide?.hero_image || "",
    read_minutes: guide?.read_minutes ?? 4,
    passages: guide?.passages || ["", "", ""],
    pull_quote: guide?.pull_quote || "",
    tags: guide?.tags || [],
    destination_tokens: guide?.destination_tokens || [],
    assigned_trip_ids: guide?.assigned_trip_ids || [],
    published: guide?.published ?? true,
  }));
  const [saving, setSaving] = React.useState(false);
  const [trips, setTrips] = React.useState(null);

  // Load advisor's trips for the assignment picker.
  React.useEffect(() => {
    api.get("/travels/trips").then(({ data }) => {
      setTrips(Array.isArray(data) ? data : (data?.trips || []));
    }).catch(() => setTrips([]));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSave = async () => {
    if (!form.slug || !/^[a-z0-9-]+$/.test(form.slug)) {
      toast.error("Slug must be lowercase letters, numbers, hyphens.");
      return;
    }
    if (!form.title || form.title.length < 2) {
      toast.error("Title required.");
      return;
    }
    if (!form.hero_image) {
      toast.error("Hero image URL required.");
      return;
    }
    const passages = form.passages.map((p) => p.trim()).filter(Boolean);
    if (passages.length < 1) {
      toast.error("At least one passage required.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, passages };
      if (isNew) {
        await api.post("/admin/curated-guides", payload);
        toast.success("Guide created.");
      } else {
        await api.put(`/admin/curated-guides/${form.slug}`, payload);
        toast.success("Guide saved.");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const addPassage = () => set("passages", [...form.passages, ""]);
  const updatePassage = (i, v) => set("passages", form.passages.map((p, idx) => idx === i ? v : p));
  const removePassage = (i) => set("passages", form.passages.filter((_, idx) => idx !== i));

  const toggleTrip = (tripId) => {
    set("assigned_trip_ids",
      form.assigned_trip_ids.includes(tripId)
        ? form.assigned_trip_ids.filter((id) => id !== tripId)
        : [...form.assigned_trip_ids, tripId],
    );
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-stretch sm:items-center justify-center sm:p-4" role="dialog" aria-modal="true" data-testid="guide-editor">
      <div className="bg-white w-full sm:max-w-3xl sm:rounded-lg overflow-hidden flex flex-col max-h-screen sm:max-h-[92vh]">
        <header className="px-5 py-4 border-b border-[rgba(0,0,0,0.06)] flex items-center justify-between gap-3 shrink-0">
          <Heading level="h3">{isNew ? "New guide" : `Edit · ${form.title || form.slug}`}</Heading>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-[hsl(var(--background-paper))]" data-testid="guide-editor-close">
            <X className="size-4" strokeWidth={1.5} />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {/* Slug + Title */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] block mb-1.5">Slug *</label>
              <input
                type="text"
                value={form.slug}
                onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
                disabled={!isNew}
                placeholder="manhattan-after-dark"
                className="w-full px-3 py-2 rounded-md border border-[rgba(0,0,0,0.08)] bg-white text-[13px] font-mono disabled:opacity-50"
                data-testid="guide-slug"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] block mb-1.5">Title *</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => set("title", e.target.value)}
                placeholder="Manhattan, After Dark"
                className="w-full px-3 py-2 rounded-md border border-[rgba(0,0,0,0.08)] bg-white font-serif text-[15px]"
                data-testid="guide-title"
              />
            </div>
          </div>

          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] block mb-1.5">Subtitle</label>
            <input
              type="text"
              value={form.subtitle}
              onChange={(e) => set("subtitle", e.target.value)}
              placeholder="The city quiets in places you wouldn't expect"
              className="w-full px-3 py-2 rounded-md border border-[rgba(0,0,0,0.08)] bg-white font-accent italic text-[14px]"
              data-testid="guide-subtitle"
            />
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] block mb-1.5">Hero image URL *</label>
              <input
                type="url"
                value={form.hero_image}
                onChange={(e) => set("hero_image", e.target.value)}
                placeholder="https://images.pexels.com/photos/…"
                className="w-full px-3 py-2 rounded-md border border-[rgba(0,0,0,0.08)] bg-white text-[13px] font-mono"
                data-testid="guide-hero"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] block mb-1.5">Read · minutes</label>
              <input
                type="number"
                min={1}
                max={30}
                value={form.read_minutes}
                onChange={(e) => set("read_minutes", parseInt(e.target.value) || 4)}
                className="w-full px-3 py-2 rounded-md border border-[rgba(0,0,0,0.08)] bg-white text-[13px]"
                data-testid="guide-readmins"
              />
            </div>
          </div>

          {form.hero_image && (
            <div className="aspect-[21/9] rounded-md overflow-hidden bg-[hsl(var(--background-paper))] relative">
              <img src={form.hero_image} alt="Hero preview" className="absolute inset-0 w-full h-full object-cover" />
            </div>
          )}

          {/* Passages */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">Passages * · 3 short paragraphs work best</label>
              <button onClick={addPassage} className="text-[11px] font-mono uppercase tracking-[0.18em] text-[hsl(var(--accent-gold))] hover:underline" data-testid="guide-add-passage">+ add passage</button>
            </div>
            <div className="space-y-2">
              {form.passages.map((p, i) => (
                <div key={i} className="flex gap-2">
                  <textarea
                    value={p}
                    onChange={(e) => updatePassage(i, e.target.value)}
                    rows={4}
                    placeholder={`Passage ${i + 1}…`}
                    className="flex-1 px-3 py-2 rounded-md border border-[rgba(0,0,0,0.08)] bg-white font-accent italic text-[13.5px] leading-relaxed resize-y"
                    data-testid={`guide-passage-${i}`}
                  />
                  {form.passages.length > 1 && (
                    <button onClick={() => removePassage(i)} className="p-1.5 self-start rounded text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-terracotta))]"><Trash2 className="size-3.5" strokeWidth={1.5} /></button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] block mb-1.5">Pull quote</label>
            <input
              type="text"
              value={form.pull_quote || ""}
              onChange={(e) => set("pull_quote", e.target.value)}
              placeholder="The city sleeps in pockets."
              className="w-full px-3 py-2 rounded-md border border-[rgba(0,0,0,0.08)] bg-white font-serif italic text-[14px]"
              data-testid="guide-pullquote"
            />
          </div>

          {/* Destination tokens */}
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] block mb-1.5">
              Destination tokens · auto-match against visible trip segments
            </label>
            <ChipInput
              value={form.destination_tokens}
              onChange={(v) => set("destination_tokens", v)}
              placeholder="antigua, barbuda, caribbean"
              testid="guide-tokens"
            />
            <p className="mt-1 font-accent italic text-[11.5px] text-[hsl(var(--foreground-muted))]">
              Lowercase city/country tokens. When any of these match a visible segment of a trip, the guide surfaces automatically.
            </p>
          </div>

          {/* Tags */}
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] block mb-1.5">Tags · editorial</label>
            <ChipInput
              value={form.tags}
              onChange={(v) => set("tags", v)}
              placeholder="caribbean, evening, private-island"
              testid="guide-tags"
            />
          </div>

          {/* Trip assignment picker */}
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] block mb-1.5">
              Explicit trip assignments · advisor override
            </label>
            <p className="font-accent italic text-[11.5px] text-[hsl(var(--foreground-muted))] mb-2">
              Pick specific trips to show this guide on, regardless of destination match.
            </p>
            <TripPicker
              trips={trips}
              selected={form.assigned_trip_ids}
              onToggle={toggleTrip}
            />
          </div>

          {/* Published */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.published}
              onChange={(e) => set("published", e.target.checked)}
              className="rounded border-[rgba(0,0,0,0.2)]"
              data-testid="guide-published"
            />
            <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--foreground))]">
              Published · visible to clients
            </span>
            {form.published ? (
              <Eye className="size-3.5 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
            ) : (
              <EyeOff className="size-3.5 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />
            )}
          </label>
        </div>
        <footer className="px-5 py-4 border-t border-[rgba(0,0,0,0.06)] flex items-center justify-end gap-2 shrink-0">
          <PortalButton variant="ghost" onClick={onClose} disabled={saving}>Cancel</PortalButton>
          <PortalButton
            onClick={onSave}
            disabled={saving}
            icon={saving ? <Loader2 className="size-3.5 animate-spin" strokeWidth={1.5} /> : <Save className="size-3.5" strokeWidth={1.5} />}
            data-testid="guide-save"
          >
            {saving ? "Saving…" : isNew ? "Create guide" : "Save"}
          </PortalButton>
        </footer>
      </div>
    </div>
  );
}

function ChipInput({ value, onChange, placeholder, testid }) {
  const [input, setInput] = React.useState("");
  const commit = () => {
    const tokens = input.split(/[,\n]+/).map((s) => s.trim().toLowerCase()).filter(Boolean);
    if (tokens.length === 0) return;
    onChange([...new Set([...value, ...tokens])]);
    setInput("");
  };
  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {value.map((v) => (
          <span key={v} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[hsl(var(--accent-gold))]/10 font-mono text-[10.5px] uppercase tracking-[0.16em] text-[hsl(var(--accent-gold))]">
            {v}
            <button onClick={() => onChange(value.filter((x) => x !== v))} className="hover:text-[hsl(var(--accent-terracotta))]" aria-label={`Remove ${v}`}>
              <X className="size-2.5" strokeWidth={2} />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-1.5">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              commit();
            }
          }}
          placeholder={placeholder}
          className="flex-1 px-3 py-1.5 rounded-md border border-[rgba(0,0,0,0.08)] bg-white text-[12.5px]"
          data-testid={testid}
        />
        <PortalButton variant="ghost" size="sm" onClick={commit}>Add</PortalButton>
      </div>
    </div>
  );
}

function TripPicker({ trips, selected, onToggle }) {
  const [q, setQ] = React.useState("");
  if (trips === null) {
    return <p className="text-[12px] text-[hsl(var(--foreground-muted))]"><Loader2 className="size-3 animate-spin inline mr-1" /> Loading trips…</p>;
  }
  const filtered = trips.filter((t) => {
    if (!q.trim()) return true;
    const text = [t.internal_trip_name, t.client_name, t.destination, t.destinations, t.id].filter(Boolean).join(" ").toLowerCase();
    return text.includes(q.toLowerCase().trim());
  });
  return (
    <div>
      <input
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search trips…"
        className="w-full px-3 py-1.5 rounded-md border border-[rgba(0,0,0,0.08)] bg-white text-[12.5px] mb-2"
      />
      <div className="max-h-44 overflow-y-auto border border-[rgba(0,0,0,0.06)] rounded-md divide-y divide-[rgba(0,0,0,0.04)]">
        {filtered.length === 0 && (
          <p className="px-3 py-2 text-[12px] text-[hsl(var(--foreground-muted))] italic">No trips match.</p>
        )}
        {filtered.slice(0, 50).map((t) => {
          const isSelected = selected.includes(t.id);
          return (
            <label
              key={t.id}
              className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-[hsl(var(--background-paper))]"
              data-testid={`trip-pick-${t.id}`}
            >
              <input type="checkbox" checked={isSelected} onChange={() => onToggle(t.id)} />
              <div className="flex-1 min-w-0">
                <p className="font-serif text-[13px] text-[hsl(var(--foreground))] truncate">
                  {t.internal_trip_name || t.title || `Trip ${t.id?.slice(0, 8)}`}
                </p>
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
                  {t.client_name || "—"} · {t.destination || t.destinations || "—"}
                </p>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}
